/* Graph of Life brand v1.1.0 · tokens.json · cinematic first-pass */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, ArrowRight, ArrowLeft } from 'lucide-react';
import { ReviewCta } from '@/components/gol/ReviewCta';
import { track } from '@/components/gol/ReviewCta';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

// A thirty second cinematic pass. Three self-contained scenes flying a buyer
// through the same argument the homepage makes at length: a living dependency
// collapses to one number, every claim is a working proof with a stated limit,
// and a review hands back an inspectable decision artifact. Ends on the Scope
// CTA. Silent, captioned, canvas-driven, static under reduced motion.

const TEAL = [45, 212, 191];
const CORAL = [240, 112, 92];
const AMBER = [232, 181, 75];
const rgba = ([r, g, b], a) => `rgba(${r}, ${g}, ${b}, ${a})`;
const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
const clamp01 = (x) => Math.max(0, Math.min(1, x));

const SCENE_MS = 10000;

const SCENES = [
  {
    key: 'dependency',
    index: '01',
    tag: 'Dependency',
    title: 'See the dependency.',
    caption:
      'A mangrove stores carbon, shelters fish and softens waves. Each of those relationships carries evidence that a single score can erase.',
  },
  {
    key: 'proof',
    index: '02',
    tag: 'Proof',
    title: 'Follow the evidence.',
    caption:
      'The working systems connect each question to its sources, methods and uncertainty. Their present limits stay open to inspection.',
  },
  {
    key: 'review',
    index: '03',
    tag: 'Review',
    title: 'Defend the decision.',
    caption:
      'Begin with one bounded question. A Dependency Exposure Review traces the pathways and prepares a decision brief with clear validation priorities.',
  },
];

// ---------- scene painters ----------

// mangrove dependency network that collapses into one score
const dependencyNodes = [
  { x: 0.5, y: 0.5, r: 8, core: true },
  { x: 0.2, y: 0.22, r: 4.5 },
  { x: 0.8, y: 0.2, r: 4.5 },
  { x: 0.86, y: 0.62, r: 4.5 },
  { x: 0.62, y: 0.84, r: 4.5 },
  { x: 0.16, y: 0.72, r: 4.5 },
  { x: 0.34, y: 0.9, r: 4, caveat: true },
];
const dependencyEdges = [
  [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [4, 6], [1, 5], [2, 3],
];

const drawDependency = (ctx, w, h, t, p) => {
  const collapse = easeInOut(clamp01((p - 0.5) / 0.45));
  const cx = w / 2;
  const cy = h / 2;
  const scale = Math.min(w, h) * 0.82;
  const ox = cx - scale / 2;
  const oy = cy - scale / 2;
  const px = (n) => ox + n.x * scale;
  const py = (n) => oy + n.y * scale;

  ctx.lineWidth = 1;
  dependencyEdges.forEach(([a, b], i) => {
    const na = dependencyNodes[a];
    const nb = dependencyNodes[b];
    const flick = 0.55 + 0.45 * Math.sin(t / 520 + i * 1.3);
    ctx.strokeStyle = rgba(TEAL, (1 - collapse) * 0.4 * flick);
    ctx.beginPath();
    ctx.moveTo(px(na), py(na));
    ctx.lineTo(px(nb), py(nb));
    ctx.stroke();
  });

  dependencyNodes.forEach((n, i) => {
    const nx = px(n) + (cx - px(n)) * collapse;
    const ny = py(n) + (cy - py(n)) * collapse;
    const pulse = n.core ? 1 + Math.sin(t / 460) * 0.18 : 1;
    const alpha = n.core ? 1 : (1 - collapse) * 0.95;
    const rad = (n.r + (n.core ? Math.sin(t / 460) * 1.4 : 0)) * pulse;
    const col = n.caveat ? CORAL : TEAL;
    ctx.shadowBlur = n.core ? 26 : 14;
    ctx.shadowColor = rgba(col, 0.7);
    ctx.fillStyle = rgba(col, alpha);
    ctx.beginPath();
    ctx.arc(nx, ny, rad, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  });

  if (collapse > 0.55) {
    const a = clamp01((collapse - 0.55) / 0.45);
    ctx.fillStyle = rgba([247, 245, 242], a * 0.92);
    ctx.font = `300 ${Math.round(Math.min(w, h) * 0.16)}px "Crimson Pro", Georgia, serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('1', cx, cy - 4);
    ctx.fillStyle = rgba(CORAL, a * 0.8);
    ctx.font = '500 11px "JetBrains Mono", monospace';
    ctx.fillText('SIX PATHWAYS · ONE CAVEAT · PATHWAY HIDDEN', cx, cy + Math.min(w, h) * 0.12);
  }
};

// governed lattice with committing evidence cells, a keystone pulse
const proofState = { nodes: null };
const drawProof = (ctx, w, h, t) => {
  if (!proofState.nodes) {
    proofState.nodes = Array.from({ length: 22 }, (_, i) => ({
      x: 0.08 + ((i * 0.137) % 0.84),
      y: 0.14 + ((i * 0.234) % 0.72),
      vx: (Math.sin(i) * 0.5) * 0.00028,
      vy: (Math.cos(i * 1.7) * 0.5) * 0.00032,
      keystone: i === 5 || i === 14,
    }));
  }
  const nodes = proofState.nodes;
  nodes.forEach((n) => {
    n.x += n.vx;
    n.y += n.vy;
    if (n.x < 0.05 || n.x > 0.95) n.vx *= -1;
    if (n.y < 0.08 || n.y > 0.9) n.vy *= -1;
  });
  const maxD = Math.min(w, h) * 0.34;
  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const a = nodes[i];
      const b = nodes[j];
      const d = Math.hypot((a.x - b.x) * w, (a.y - b.y) * h);
      if (d > maxD) continue;
      const flick = 0.5 + 0.5 * Math.sin(t / 420 + i + j);
      ctx.strokeStyle = rgba(TEAL, (1 - d / maxD) * 0.3 * flick);
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(a.x * w, a.y * h);
      ctx.lineTo(b.x * w, b.y * h);
      ctx.stroke();
    }
  }
  nodes.forEach((n, i) => {
    const pulse = n.keystone ? 3 + Math.sin(t / 300 + i) * 1.6 : 1.8;
    ctx.shadowBlur = n.keystone ? 22 : 8;
    ctx.shadowColor = rgba(TEAL, 0.8);
    ctx.fillStyle = rgba(TEAL, n.keystone ? 0.95 : 0.55);
    ctx.beginPath();
    ctx.arc(n.x * w, n.y * h, pulse, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.shadowBlur = 0;
};

// the decision artifact assembling line by line
const artifactLines = [
  { w: 0.62, kind: 'head' },
  { w: 0.9, kind: 'body' },
  { w: 0.84, kind: 'body' },
  { w: 0.7, kind: 'body' },
  { w: 0.5, kind: 'gap' },
  { w: 0.88, kind: 'body' },
  { w: 0.46, kind: 'caveat' },
];
const drawReview = (ctx, w, h, t, p) => {
  const docW = Math.min(w * 0.6, 460);
  const docH = Math.min(h * 0.74, 360);
  const ox = (w - docW) / 2;
  const oy = (h - docH) / 2;

  ctx.strokeStyle = rgba(TEAL, 0.3 + 0.15 * Math.sin(t / 700));
  ctx.lineWidth = 1;
  ctx.strokeRect(ox, oy, docW, docH);
  ctx.fillStyle = rgba(TEAL, 0.03);
  ctx.fillRect(ox, oy, docW, docH);

  const pad = docW * 0.09;
  const usable = docH - pad * 2;
  const rowH = usable / artifactLines.length;
  artifactLines.forEach((ln, i) => {
    const appear = clamp01((p - i * 0.11) / 0.16);
    if (appear <= 0) return;
    const y = oy + pad + i * rowH + rowH * 0.4;
    const lw = docW - pad * 2;
    if (ln.kind === 'gap') return;
    if (ln.kind === 'head') {
      ctx.fillStyle = rgba(TEAL, 0.85 * appear);
      ctx.fillRect(ox + pad, y - 6, lw * ln.w * appear, 7);
    } else if (ln.kind === 'caveat') {
      ctx.fillStyle = rgba(CORAL, 0.75 * appear);
      ctx.fillRect(ox + pad, y - 3, lw * ln.w * appear, 4);
    } else {
      ctx.fillStyle = rgba([168, 180, 199], 0.5 * appear);
      ctx.fillRect(ox + pad, y - 3, lw * ln.w * appear, 4);
    }
  });

  const stamp = clamp01((p - 0.82) / 0.18);
  if (stamp > 0) {
    ctx.strokeStyle = rgba(AMBER, 0.85 * stamp);
    ctx.lineWidth = 1.4;
    const sr = 26;
    const sx = ox + docW - pad - sr;
    const sy = oy + docH - pad - sr;
    ctx.beginPath();
    ctx.arc(sx, sy, sr * (0.6 + stamp * 0.4), 0, Math.PI * 2 * stamp);
    ctx.stroke();
  }
};

const painters = {
  dependency: drawDependency,
  proof: drawProof,
  review: drawReview,
};

// ---------- overlay ----------

export const GuidedWalkthrough = ({ open, onClose }) => {
  const [scene, setScene] = useState(0); // 0..2 scenes, 3 = handoff
  const [paused, setPaused] = useState(false);
  const canvasRef = useRef(null);
  const barRef = useRef(null);
  const elapsedRef = useRef(0);
  const pausedRef = useRef(false);
  const sceneRef = useRef(0);

  pausedRef.current = paused;
  sceneRef.current = scene;
  const modalRef = useRef(null);
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const focusFrame = requestAnimationFrame(() => modalRef.current?.querySelector('[data-testid="walkthrough-close"]')?.focus());
    const trap = (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(modalRef.current?.querySelectorAll('button:not([disabled]), a[href]') || []).filter(el => el.getClientRects().length);
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !modalRef.current?.contains(document.activeElement))) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !modalRef.current?.contains(document.activeElement))) { e.preventDefault(); first?.focus(); }
    };
    window.addEventListener('keydown', trap);
    return () => { cancelAnimationFrame(focusFrame); window.removeEventListener('keydown', trap); if (opener?.isConnected) opener.focus(); };
  }, [open]);

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const goto = useCallback((next) => {
    elapsedRef.current = 0;
    if (barRef.current) barRef.current.style.transform = 'scaleX(0)';
    setScene(next);
  }, []);

  const close = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // reset when opened
  useEffect(() => {
    if (open) {
      proofState.nodes = null;
      elapsedRef.current = 0;
      setPaused(false);
      setScene(0);
      track('walkthrough_start', { source: 'launcher' });
    }
  }, [open]);

  // lock scroll + escape to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight' && sceneRef.current < 3) goto(Math.min(sceneRef.current + 1, 3));
      if (e.key === 'ArrowLeft' && sceneRef.current > 0) goto(sceneRef.current - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close, goto]);

  // animation + auto-advance loop
  useEffect(() => {
    if (!open || scene > 2) return undefined;
    let last = performance.now();
    const tick = () => {
      const now = performance.now();
      if (!pausedRef.current && !document.hidden) elapsedRef.current += now - last;
      last = now;
      if (elapsedRef.current >= SCENE_MS) goto(scene + 1);
    };
    const resetClock = () => { last = performance.now(); };
    const timer = setInterval(tick, 50);
    document.addEventListener('visibilitychange', resetClock);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', resetClock); };
  }, [open, scene, goto]);

  useEffect(() => {
    if (!open || scene > 2) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const paint = painters[SCENES[scene].key];
    let raf;
    let w = 0;
    let h = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (reduced) paint(ctx, w, h, 1200, 0.7);
    };
    resize();
    window.addEventListener('resize', resize);

    if (reduced) {
      // single static frame, timed slideshow with no animation
      ctx.clearRect(0, 0, w, h);
      paint(ctx, w, h, 1200, 0.7);
      if (barRef.current) barRef.current.style.transform = 'scaleX(1)';
      return () => {
        window.removeEventListener('resize', resize);
      };
    }

    const loop = (now) => {
      const p = clamp01(elapsedRef.current / SCENE_MS);
      ctx.clearRect(0, 0, w, h);
      paint(ctx, w, h, elapsedRef.current, p);
      if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [open, scene, reduced, goto]);

  if (!open) return null;
  const active = SCENES[scene] || null;
  const atEnd = scene > 2;

  return createPortal(
    <motion.div
      ref={modalRef}
      data-testid="walkthrough-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Thirty second guided walkthrough"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[80] bg-abyss-950/96 backdrop-blur-xl overflow-hidden"
    >
      <div className="gol-grid absolute inset-0 opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="gol-scan absolute inset-0 pointer-events-none" aria-hidden="true" />

      {/* scene progress rail */}
      <div className="absolute top-0 left-0 right-0 z-20 flex gap-1.5 px-5 md:px-8 pt-4" aria-hidden="true">
        {SCENES.map((s, i) => (
          <div key={s.key} className="h-[3px] flex-1 bg-abyss-800 overflow-hidden rounded-full">
            <div
              ref={i === scene ? barRef : null}
              className="h-full origin-left bg-teal-400 shadow-[0_0_10px_rgba(45,212,191,0.7)]"
              style={{ transform: i < scene ? 'scaleX(1)' : 'scaleX(0)' }}
            />
          </div>
        ))}
      </div>

      {/* top controls */}
      <div className="absolute top-8 left-0 right-0 z-20 flex items-center justify-between px-5 md:px-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal-400/70">
          Guided pass · 30s
        </span>
        <div className="flex items-center gap-4">
          {!atEnd && (
            <button
              type="button"
              onClick={() => setPaused((v) => !v)}
              data-testid="walkthrough-pause"
              aria-label={paused ? 'Resume walkthrough' : 'Pause walkthrough'}
              className="p-2 text-sand-50/60 hover:text-teal-300 transition-colors duration-300"
            >
              {paused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              track('walkthrough_skip', { scene: atEnd ? 'end' : SCENES[scene].key });
              close();
            }}
            data-testid="walkthrough-close"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-sand-50/50 hover:text-teal-300 transition-colors duration-300"
          >
            {atEnd ? 'Close' : 'Skip tour'}
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* stage */}
      <div className="relative z-10 h-full w-full flex items-center justify-center px-5 md:px-8">
        {!atEnd ? (
          <div className="w-full max-w-5xl grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14 items-center">
            {/* canvas scene */}
            <div className="relative aspect-square sm:aspect-[4/3] w-full gol-panel gol-panel-lead overflow-hidden">
              <canvas
                ref={canvasRef}
                data-testid="walkthrough-canvas"
                aria-hidden="true"
                role="presentation"
                className="absolute inset-0 w-full h-full"
              />
              <span className="absolute left-4 top-3 font-mono text-[9px] uppercase tracking-[0.2em] text-teal-400/60">
                Field {active.index} / 03
              </span>
            </div>

            {/* caption */}
            <AnimatePresence mode="wait">
              <motion.div
                key={active.key}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="space-y-5"
                data-testid={`walkthrough-scene-${active.key}`}
              >
                <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-sm border border-teal-500/25 bg-teal-500/5">
                  <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-400" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal-400">
                    {active.index} · {active.tag}
                  </span>
                </div>
                <h2 className="font-serif text-4xl sm:text-5xl font-light leading-[1.05] tracking-tight text-sand-50">
                  {active.title}
                </h2>
                <p className="text-base md:text-lg font-light leading-relaxed text-sand-50/70 max-w-md">
                  {active.caption}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-2xl text-center space-y-9"
            data-testid="walkthrough-handoff"
          >
            <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-sm border border-teal-500/25 bg-teal-500/5 mx-auto">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal-400">
                End of pass
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-[1.06] tracking-tight text-sand-50">
              Bring one decision that
              <br />
              <span className="text-teal-400 gol-glow">depends on nature.</span>
            </h2>
            <p className="text-base md:text-lg font-light leading-relaxed text-sand-50/70 max-w-lg mx-auto">
              That is the whole engagement in thirty seconds. A Dependency Exposure Review is where it begins.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <ReviewCta location="walkthrough-end" testId="walkthrough-scope-review" onClick={() => { track('walkthrough_convert', {}); close(); }} />
              <button
                type="button"
                onClick={() => { setPaused(false); goto(0); }}
                data-testid="walkthrough-replay"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-sm border border-sand-50/15 font-mono text-xs uppercase tracking-[0.16em] text-sand-50/60 hover:text-teal-300 hover:border-teal-500/40 transition-all duration-300"
              >
                <Play size={13} aria-hidden="true" />
                Replay the pass
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* bottom scene nav */}
      {!atEnd && (
        <div className="absolute bottom-8 left-0 right-0 z-20 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={() => goto(scene - 1)}
            disabled={scene === 0}
            data-testid="walkthrough-prev"
            aria-label="Previous scene"
            className="p-2 text-sand-50/50 hover:text-teal-300 disabled:opacity-20 disabled:hover:text-sand-50/50 transition-colors duration-300"
          >
            <ArrowLeft size={16} aria-hidden="true" />
          </button>
          <div className="flex items-center gap-2.5">
            {SCENES.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => goto(i)}
                data-testid={`walkthrough-dot-${i}`}
                aria-label={`Go to scene ${i + 1}, ${s.tag}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === scene ? 'w-6 bg-teal-400' : 'w-1.5 bg-sand-50/25 hover:bg-sand-50/50'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => goto(scene + 1)}
            data-testid="walkthrough-next"
            aria-label="Next scene"
            className="p-2 text-sand-50/50 hover:text-teal-300 transition-colors duration-300"
          >
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}
    </motion.div>,
    document.body
  );
};

export default GuidedWalkthrough;
